class ObjectStore {
    constructor(objectStore) {
        this.#objectStore = objectStore
    }

    async add(item, key) {
        const {promise, reject, resolve} = Promise.withResolvers()
        const request = this.#objectStore.add(item, key)
        request.onerror = e => reject(e.target.error)
        request.oncomplete = e => resolve(e.target.result)
        return promise
    }

    async delete(key) {
        const {promise, reject, resolve} = Promise.withResolvers()
        const request = this.#objectStore.delete(key)
        request.onerror = e => reject(e.target.error)
        request.onsuccess = e => resolve(e.target.result)
        return promise
    }

    async get(key) {
        const {promise, reject, resolve} = Promise.withResolvers()
        const request = this.#objectStore.get(key)
        request.onerror = e => reject(e.target.error)
        request.onsuccess = e => resolve(e.target.result)
        return promise
    }

    async *openCursor(query, direction) {
        const request = this.#objectStore.openCursor(query, direction)

        for await (const c of this.#openCursor(request)) {
            yield c
            c.continue()
        }
    }

    async *openCursorOnIndex(indexName, query, direction) {
        const index = this.#objectStore.index(indexName)
        const request = index.openCursor(query, direction)

        for await (const c of this.#openCursor(request)) {
            yield c
            c.continue()
        }
    }

    async put(item, key) {
        const {promise, reject, resolve} = Promise.withResolvers()
        const request = this.#objectStore.put(item, key)
        request.onerror = e => reject(e.target.error)
        request.onsuccess = e => resolve(e.target.result)
        return promise
    }

    #openCursor(request) {
        let r

        const it = {
            next() {
                r = Promise.withResolvers()
                return r.promise
            },

            [Symbol.asyncIterator]() {
                return this
            },
        }

        request.onerror = e => r.reject(e.target.error)

        request.onsuccess = e => {
            const cursor = e.target.result

            if (cursor) {
                r.resolve({value: cursor})
            }
            else {
                r.resolve({done: true})
            }
        }

        return it
    }

    #objectStore
}

class Transaction {
    constructor(db, storeNames, mode, options) {
        this.#db = db
        this.#init = [storeNames, mode, options]
    }

    async do(fn) {
        const db = await this.#db
        this.#transaction = db.transaction(...this.#init)
        const {promise, reject, resolve} = Promise.withResolvers()
        let result
        this.#transaction.onerror = e => reject(e.target.error)
        this.#transaction.oncomplete = e => resolve(result)

        try {
            result = await fn(this)
        }
        catch (err) {
            reject(err)
        }

        return promise
    }

    objectStore(storeName) {
        const objectStore = this.#transaction.objectStore(storeName)
        return new ObjectStore(objectStore)
    }

    #db
    #init
    #transaction
}

export default class IndexedDb {
    constructor(name, version) {
        this.#name = name
        this.#version = version
    }

    async add(storeName, item, key) {
        return this.transaction(storeName, 'readwrite').do(t => {
            const store = t.objectStore(storeName)
            return store.add(item, key)
        })
    }

    async delete(storeName, key) {
        return this.transaction(storeName, 'readwrite').do(t => {
            const store = t.objectStore(storeName)
            return store.delete(key)
        })
    }

    async get(storeName, key) {
        return this.transaction(storeName, 'readonly').do(t => {
            const store = t.objectStore(storeName)
            return store.get(key)
        })
    }

    open(onupgradeneeded) {
        this.#db = new Promise((resolve, reject) => {
            const request = indexedDB.open(this.#name, this.#version)
            request.onerror = e => reject(e.target.error)
            request.onsuccess = e => resolve(e.target.result)

            request.onupgradeneeded = e => {
                const db = e.target.result
                onupgradeneeded(db)
            }
        })
    }

    async put(storeName, item, key) {
        return this.transaction(storeName, 'readwrite').do(t => {
            const store = t.objectStore(storeName)
            return store.put(item, key)
        })
    }

    transaction(storeNames, mode, options) {
        return new Transaction(this.#db, storeNames, mode, options)
    }

    #db
    #name
    #version
}
